// Modal ký số: mở PDF biên bản, đặt vùng ký cho hai bên rồi lưu qua agreement-save.
import { yupResolver } from "@hookform/resolvers/yup";
import AgreementSignZonePanel from "components/AgreementSignZone/AgreementSignZonePanel";
import AgreementSignZoneViewer from "components/AgreementSignZone/AgreementSignZoneViewer";
import MessageBox from "components/shared-components/MessageBox";
import { AGREEMENT_REPORT_TITLE } from "constants/AgreementReportConstant";
import { useAgreementPdfUrl } from "hooks/invoiceAgreement/useAgreementPdfUrl";
import {
  useGetAgreementList,
  useSaveAgreementInfo,
  useSendAgreementSignMail,
  useSignAgreement,
} from "hooks/invoiceAgreement/useInvoiceAgreement";
import { buildSignZonesSeed } from "pages/IssueInvoice/helper/agreementSignZone";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useToasts } from "react-toast-notifications";
import {
  Button,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import * as yup from "yup";

const MSG_ZONE_REQUIRED = "Vui lòng thêm ít nhất một vùng ký";
const MSG_ZONE_NOT_DRAWN = "Vui lòng vẽ vùng ký trên PDF";
const MSG_ZONE_TOO_LARGE = "Vùng ký quá lớn (tối đa 500)";

// Tên field email gửi kèm khi lưu biên bản. Backend đổi tên thì sửa đúng dòng này.
const SIGN_EMAIL_FIELD = "sign_email";

// Chờ bên B ký: mssigning ký xong rồi callback về mới cập nhật b_signed_at.
// Thực đo trên UAT khoảng vài giây; để 60s cho chắc, quá thì báo người dùng
// kiểm lại sau chứ không coi là lỗi (job vẫn đang chạy).
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_TRIES = 30;

const signEmailSchema = yup
  .string()
  .trim()
  .required("Vui lòng nhập email")
  .email("Email không đúng định dạng");

const signZoneSchema = yup.object().shape({
  sign_zones: yup.array().of(
    yup.object().shape({
      zone_infos: yup
        .array()
        .min(1, MSG_ZONE_REQUIRED)
        .of(
          yup.object().shape({
            zone_width: yup
              .number()
              .min(1, MSG_ZONE_NOT_DRAWN)
              .max(500, MSG_ZONE_TOO_LARGE),
            zone_height: yup.number().min(1, MSG_ZONE_NOT_DRAWN),
          })
        ),
    })
  ),
});

const AgreementSignZoneModal = props => {
  const { isOpen, toggle, invoiceId, typeReport, rawData, onSaved } = props;

  const { addToast } = useToasts();

  const [handleActionDocument, setHandleActionDocument] = useState(null);

  // Modal nhỏ nhập email, hiện ra sau khi vùng ký đã hợp lệ
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [signEmail, setSignEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const { pdfUrl, isError, isWaitTooLong, retry } = useAgreementPdfUrl({
    isOpen,
    invoiceId,
    typeReport,
    agreementId: rawData?.agreement_id,
    // Modal cha vừa nạp lại biên bản -> PDF đã xong thì khỏi gọi agreement-info
    agreementInfo: rawData,
  });

  const mainForm = useForm({
    resolver: yupResolver(signZoneSchema),
    mode: "all",
    defaultValues: {
      sign_zones: buildSignZonesSeed(null),
      pdfJsLoaded: false,
    },
  });

  const { reset, trigger, getValues, watch } = mainForm;

  const pdfJsLoaded = watch("pdfJsLoaded");

  // Trình xem PDF không chịu báo đã nạp xong -> cho nạp lại thay vì đứng hình
  const [viewerNonce, setViewerNonce] = useState(0);
  const [isViewerStuck, setIsViewerStuck] = useState(false);

  const reloadViewer = () => {
    setIsViewerStuck(false);
    setViewerNonce(Date.now());
  };

  // Mở modal thì nạp lại vùng ký từ đầu
  useEffect(() => {
    if (isOpen) {
      setHandleActionDocument(null);
      setIsEmailOpen(false);
      setSignEmail("");
      setEmailError("");
      setIsViewerStuck(false);
      setViewerNonce(0);
      reset({
        sign_zones: buildSignZonesSeed(rawData?.sign_zones),
        pdfJsLoaded: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Chuỗi ký số ───────────────────────────────────────────────────────────
  // Trước đây nút "Ký số" chỉ lưu vùng ký rồi dừng; hai bước còn lại phải chạy
  // tay bằng curl/Postman. Nay nối tiếp luôn:
  //   agreement-save -> agreement-sign -> (chờ bên B ký) -> agreement-send-mail
  const { mutateAsync: signAgreementAsync } = useSignAgreement({});
  const { mutateAsync: getAgreementListAsync } = useGetAgreementList({});
  const { mutateAsync: sendSignMailAsync } = useSendAgreementSignMail({});

  const [signProgress, setSignProgress] = useState("");

  // Chờ bên B ký xong. mssigning xử lý bất đồng bộ nên phải hỏi lại nhiều lần.
  // Poll agreement-list (CHỈ ĐỌC) chứ KHÔNG poll agreement-sign-info: endpoint
  // đó khi thấy b_signed_at còn rỗng sẽ sinh lại PDF và đẩy THÊM một job ký nữa
  // mỗi lần gọi.
  const waitPartyBSigned = async (agreementId) => {
    for (let i = 0; i < POLL_MAX_TRIES; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

      // eslint-disable-next-line no-await-in-loop
      const res = await getAgreementListAsync({ id: invoiceId });
      const items = res?.data?.data?.items || res?.data?.items || [];
      const found = items.find(
        (x) => Number(x.agreement_id) === Number(agreementId)
      );

      if (found?.b_signed_at) {
        return true;
      }
      setSignProgress(
        `Đang chờ ký số bên B… (${i + 1}/${POLL_MAX_TRIES})`
      );
    }
    return false;
  };

  const runSignFlow = async (agreementId, email) => {
    try {
      setSignProgress("Đang ký số bên B…");
      await signAgreementAsync({ agreement_id: agreementId });

      const signed = await waitPartyBSigned(agreementId);
      if (!signed) {
        // Job đã đẩy sang mssigning, chỉ là chưa xong trong thời gian chờ.
        // Không coi là thất bại để tránh người dùng bấm lại và ký chồng.
        addToast(
          "Đã gửi yêu cầu ký số bên B nhưng chưa nhận được kết quả. " +
            "Mở lại danh sách biên bản sau ít phút để kiểm tra.",
          { appearance: "warning", autoDismiss: false }
        );
        return;
      }

      setSignProgress("Đang tạo phiên ký và gửi mail cho bên mua…");
      await sendSignMailAsync({ agreement_id: agreementId, to_mail: email });

      addToast(`Đã ký số bên B và gửi link ký tới ${email}`, {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      // Lỗi HTTP thì useMutationCommon đã hiện toast rồi. Nhưng lỗi JS (gõ sai
      // tên hàm, đọc thuộc tính của undefined) không qua đó, nuốt trọn là chuỗi
      // ký chết lặng. Ít nhất phải để lại dấu vết.
      console.error("AgreementSignZoneModal runSignFlow lỗi", error);
    } finally {
      setSignProgress("");
    }
  };

  const { mutate: mutateSaveAgreementInfo, isLoading: isLoadingSave } =
    useSaveAgreementInfo({
      // useMutationCommon gọi callback bằng MỘT object { data, variables },
      // không phải hai tham số kiểu react-query. Viết (res, variables) là
      // variables luôn undefined -> mất email -> chuỗi ký im lặng bỏ chạy.
      //
      // variables là payload đã gửi đi. Lấy email từ đây chứ không đọc state
      // signEmail: closure onSuccess tạo lúc render nên có thể giữ giá trị cũ.
      onSuccess: async ({ data, variables }) => {
        // KHÔNG đóng modal email ở đây: phần hiển thị tiến trình nằm trong
        // ModalBody của nó, đóng sớm là người dùng không thấy chuỗi ký chạy tới
        // đâu. Đóng ở cuối, sau khi chuỗi kết thúc.
        const agreementId = data?.data?.agreement_id || data?.agreement_id;
        const email = variables?.data?.[SIGN_EMAIL_FIELD];

        if (!agreementId || !email) {
          // Lưu được nhưng thiếu id hoặc email thì không ký tiếp được. Phải
          // NÓI RA: bản đầu lặng lẽ đóng modal nên nhìn y hệt ký thành công,
          // trong khi biên bản chưa ký gì cả.
          addToast(
            "Đã lưu vùng ký nhưng không lấy được thông tin để ký số. " +
              "Vui lòng mở lại và thử lại.",
            { appearance: "warning", autoDismiss: false }
          );
          setIsEmailOpen(false);
          onSaved && onSaved();
          toggle();
          return;
        }

        await runSignFlow(agreementId, email);

        setIsEmailOpen(false);
        onSaved && onSaved();
        toggle();
      },
    });

  // Đang lưu HOẶC đang chạy chuỗi ký -> khoá thao tác. Chỉ dựa vào isLoadingSave
  // là hở: lưu xong rồi nhưng chuỗi ký còn chạy tiếp vài chục giây.
  const isBusy = isLoadingSave || !!signProgress;

  // Bước 1: vùng ký hợp lệ rồi mới hỏi email, tránh bắt nhập rồi báo lỗi vùng ký
  const handleSave = async () => {
    const isValid = await trigger(["sign_zones"]);

    if (!isValid) {
      addToast("Vui lòng kiểm tra lại các vùng ký chưa hợp lệ", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    setSignEmail(
      rawData?.party_a?.before?.email || rawData?.party_a?.after?.email || ""
    );
    setEmailError("");
    setIsEmailOpen(true);
  };

  // Bước 2: email hợp lệ thì lưu vùng ký kèm email
  const handleConfirmEmail = async () => {
    let email = "";

    try {
      email = await signEmailSchema.validate(signEmail);
    } catch (error) {
      setEmailError(error.message);
      return;
    }

    setEmailError("");
    const { sign_zones } = getValues();
    console.log("AgreementSignZoneModal handleConfirmEmail payload", {
      sign_zones,
      email,
    });
    mutateSaveAgreementInfo({
      invoice_id: invoiceId,
      type: typeReport,
      data: { ...rawData, sign_zones, [SIGN_EMAIL_FIELD]: email },
    });
  };

  const closeEmailModal = () => {
    // Không cho đóng giữa chừng: chuỗi ký vẫn chạy nền, đóng đi là mất luôn
    // phần hiển thị tiến trình và người dùng không biết nó tới đâu.
    if (isBusy) {
      return;
    }
    setIsEmailOpen(false);
  };

  const styleCenterBox = {
    width: "100%",
    height: "75vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#8080802b",
  };

  const renderPdfArea = () => {
    if (isError) {
      return (
        <div style={styleCenterBox}>
          <MessageBox
            variant="danger"
            classMore="d-sm-flex align-items-center justify-content-center gap-1 font-size-18"
          >
            <i className="bx bxs-error-alt"></i>Không tải được file biên bản
          </MessageBox>
        </div>
      );
    }

    if (isWaitTooLong) {
      return (
        <div style={styleCenterBox}>
          <MessageBox
            variant="warning"
            classMore="d-sm-flex align-items-center justify-content-center gap-1 font-size-18"
          >
            <i className="bx bx-time-five"></i>File biên bản đang được tạo, vui
            lòng thử lại
          </MessageBox>
        </div>
      );
    }

    if (!pdfUrl) {
      return (
        <div style={styleCenterBox}>
          <div className="d-flex flex-column align-items-center gap-2">
            <Spinner color="primary" />
            <span>Đang tạo file biên bản...</span>
          </div>
        </div>
      );
    }

    return (
      <React.Fragment>
        {isViewerStuck && (
          <MessageBox
            variant="warning"
            classMore="d-sm-flex align-items-center justify-content-center gap-1 mb-2"
          >
            <i className="bx bx-time-five"></i>Trình xem PDF nạp không xong. Bấm
            &quot;Nạp lại trình xem&quot; rồi thử lại.
          </MessageBox>
        )}
        <AgreementSignZoneViewer
          mainForm={mainForm}
          filePdfUrl={pdfUrl}
          handleActionDocument={handleActionDocument}
          style={{ height: "75vh" }}
          reloadNonce={viewerNonce}
          onLoadTimeout={() => setIsViewerStuck(true)}
        />
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <Modal
        size="xl"
        style={{ minWidth: "95vw" }}
        isOpen={isOpen}
        role="dialog"
        autoFocus={true}
        centered={true}
        toggle={toggle}
      >
        <div className="modal-content">
          <ModalHeader toggle={toggle}>
            Xác nhận ký số — {AGREEMENT_REPORT_TITLE[typeReport]}
          </ModalHeader>

          <ModalBody style={{ overflow: "hidden" }}>
            <Row>
              <Col xs="12" lg="8">
                {renderPdfArea()}
              </Col>
              <Col
                xs="12"
                lg="4"
                style={{ maxHeight: "75vh", overflow: "auto" }}
              >
                <AgreementSignZonePanel
                  mainForm={mainForm}
                  setHandleActionDocument={setHandleActionDocument}
                  isDisabled={!pdfJsLoaded || isBusy}
                  isPdfReady={pdfJsLoaded}
                  typeReport={typeReport}
                />
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              color="secondary"
              disabled={isBusy}
              onClick={toggle}
            >
              Đóng
            </Button>
            {(isWaitTooLong || isError) && (
              <Button
                type="button"
                color="primary"
                className="btn-label"
                onClick={retry}
              >
                <i className="bx bx-refresh label-icon"></i>
                Thử lại
              </Button>
            )}
            {isViewerStuck && !isWaitTooLong && !isError && (
              <Button
                type="button"
                color="primary"
                className="btn-label"
                disabled={isBusy}
                onClick={reloadViewer}
              >
                <i className="bx bx-refresh label-icon"></i>
                Nạp lại trình xem
              </Button>
            )}
            <Button
              type="button"
              color="success"
              className="btn-label"
              disabled={!pdfJsLoaded || isBusy}
              onClick={handleSave}
            >
              <i className="fas fa-file-signature label-icon"></i>
              Xác nhận ký số
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      <Modal
        isOpen={isOpen && isEmailOpen}
        toggle={closeEmailModal}
        centered={true}
        autoFocus={false}
      >
        <ModalHeader toggle={closeEmailModal}>Email nhận yêu cầu ký</ModalHeader>

        <ModalBody>
          <Label for="sign-email" className="form-label">
            Email Bên A <span className="text-danger">*</span>
          </Label>
          <Input
            id="sign-email"
            type="email"
            autoFocus
            value={signEmail}
            invalid={!!emailError}
            disabled={isBusy}
            placeholder="Nhập email nhận yêu cầu ký"
            onChange={event => {
              setSignEmail(event.target.value);
              setEmailError("");
            }}
            onKeyDown={event => {
              if (event.key === "Enter") {
                handleConfirmEmail();
              }
            }}
          />
          <FormFeedback>{emailError}</FormFeedback>

          {/* Chuỗi ký chạy nhiều bước và có đoạn chờ mssigning, không hiện gì
              thì người dùng tưởng treo rồi bấm lại -> ký chồng. */}
          {signProgress && (
            <div className="mt-3 d-flex align-items-center text-muted">
              <Spinner size="sm" className="me-2" />
              <span>{signProgress}</span>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            color="secondary"
            disabled={isBusy}
            onClick={closeEmailModal}
          >
            Huỷ
          </Button>
          <Button
            type="button"
            color="success"
            className="btn-label"
            disabled={isBusy}
            onClick={handleConfirmEmail}
          >
            {isBusy ? (
              <i className="bx bx-loader bx-spin label-icon" />
            ) : (
              <i className="fas fa-file-signature label-icon"></i>
            )}
            Xác nhận
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

AgreementSignZoneModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  invoiceId: PropTypes.number,
  typeReport: PropTypes.string,
  rawData: PropTypes.object,
  onSaved: PropTypes.func,
};

AgreementSignZoneModal.defaultProps = {
  rawData: null,
};

export default AgreementSignZoneModal;
